import { useState } from 'react';
import { addMonths } from 'date-fns';
import motard from '@/assets/images/questions/motard.png';
import plusVert from '@/assets/images/questions/picto-plus-vert.png';
import FormReleveInformation from "@/pages/questions/liste-questions/components/releve-information/form-releve-information";
import APILink from "@/components/shared/api-link";
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { useContractsData, useSelectedContract } from '@/hooks/api/use-contracts';
import { useCurrentUser } from '@/hooks/api/use-user';
import { useWhitelabel } from '@/hooks/api/use-whitelabel';
import { getActionBlockedReason, hasContractApporteur43397 as hasContractApporteur43397Fn, isActionAvailable } from '@/lib/contracts';
import { PDF } from '@/types/api/endpoints';
import { toDate } from '@/lib/dates';

const ReleveInformation = () => {
    const contract = useSelectedContract();
    const riLink = contract && isActionAvailable(contract, 'editer_ri') ? PDF.releveInformation(contract.id) : null;
    const statutCode = contract?.situation?.statutCode;
    const riReason = getActionBlockedReason(contract, 'editer_ri');
    const emailGestion = useWhitelabel().data?.email_gestion;
    const nameUser = useCurrentUser().firstname;
    const apporteur1 = contract?.apporteur1;
    const cylindree = contract?.vehicule?.cylindree;
    const urlFormulaire = contract?.souscription_link;
    const motif = contract?.situation?.motif;
    const dateFin = contract?.situation?.dateFin;
    const contractId = contract?.id;
    const hasContractApporteur43397 = hasContractApporteur43397Fn(useContractsData());
    const [disabled, setDisabled] = useState(false)

    const handleClick = () => {
        setDisabled(true)
    }

    if (riLink !== null && statutCode === '6') {
        return (
            <div className="text-center">
                <APILink
                    href={riLink as string}
                    target="_blank"
                    onClick={() => handleClick()}
                    className={`inline-flex items-center gap-2 px-6 py-2 text-sm font-medium rounded-md bg-primary text-white hover:bg-primary-hover transition-all duration-300 ease-in-out cursor-pointer no-underline ${disabled && 'opacity-50 pointer-events-none'}`}
                    download
                >
                    <Download className="w-4 h-4" />
                    Télécharger mon Relevé de sinistres
                </APILink>
                {
                    String(apporteur1) === '43397' &&
                    Number(cylindree) > 50 &&
                    motif === 'Résiliation suite à vente' &&
                    addMonths(toDate(dateFin), 4).getTime() >= Date.now() &&
                    <div className="mt-12 p-6 rounded-lg text-left border bg-muted/50">
                        <img src={motard} alt="Image de motard" width={100} className="mb-4 block mx-auto" />
                        <p className="font-bold">{nameUser}, vous nous avez récemment quitté et vous craquez pour un nouveau 2-roues.</p>
                        <p>N'attendez pas pour en profiter&nbsp;!</p>
                        <p>Comme vous faites toujours partie de la famille, on vous simplifie le retour.</p>
                        <p><b>Seule votre nouvelle carte grise<sup>*</sup></b> nous suffit pour vous assurer. Pour le reste, on s'occupe de tout&nbsp;!</p>
                        <p><img src={plusVert} alt="+" width={30} className="mr-2 inline-block" /><b>En plus on conserve vos avantages clients.</b> Votre bonus et votre crédit fidélité franchise restent valables pendant 4 mois et vous bénéficiez d'offres privilèges<sup>**</sup> pour vous équiper chez nos partenaires (Cosmo, In&motion, Bering…)</p>
                        <p className="m-0 font-bold">Comment en profiter&nbsp;?</p>
                        <p>Rien de plus simple&nbsp;! Rendez-vous sur le formulaire de souscription avec votre <b>nouvelle carte grise.</b></p>
                        <p className="mb-6 font-bold">Ne partez pas sans faire un devis.</p>
                        <small className="italic block text-muted-foreground"><sup>*</sup>Ou tout document justifiant de l'achat de votre véhicule (certificat de vente, carte grise barrée, facture d'achat, attestation d'enregistrement à la préfecture).</small>
                        <small className="mb-6 italic block text-muted-foreground"><sup>**</sup>Pour toute nouvelle souscription dans les 4 mois suivant la résiliation pour vente de votre précédent contrat.</small>

                        <div className="text-center mt-6">
                            <Button asChild variant="outline" className="cursor-pointer">
                                <a href={urlFormulaire} target="_blank" rel="noopener noreferrer">Testez nos <b>nouveaux tarifs !</b></a>
                            </Button>
                        </div>
                    </div>
                }
            </div>
        );
    } else if (riLink !== null && statutCode !== '6') {
        return (
            <div className="text-center">
                <p className="text-sm mb-4">Pour télécharger votre document, sélectionnez votre contrat, un motif puis cliquez sur le bouton ci-dessous :</p>
                <FormReleveInformation riLink={riLink as string} contractId={contractId} hasContractApporteur43397={hasContractApporteur43397} />
            </div>
        );
    } else {
        return (
            <div className="text-sm">
                <p>Votre contrat ne permet pas l'édition d'un Relevé de sinistres en ligne pour le motif suivant : <br /><strong>{riReason}</strong></p>
                <p>Vous pouvez contacter le service client à <a href={`mailto:${emailGestion}`} className="text-primary hover:underline">{emailGestion}</a></p>
            </div>
        );
    }
};

export default ReleveInformation;
